import { Button, ButtonGroup } from "@mui/material";
import { alpha } from "@mui/material/styles";
import React from "react";

import { appTheme } from "~/ui/theme";

export interface ToggleOption<T> {
  label: React.ReactNode;
  value: T;
}

export interface ToggleProps<T> {
  options: ToggleOption<T>[];
  value: T;
  onChange: (value: T) => void;
  buttonStyles?: object;
}

const DEFAULT_BUTTON_STYLES = {
  textTransform: "none" as const,
  fontWeight: 600,
  px: 3,
  py: 1,
  borderRadius: 2,
  fontSize: "0.875rem",
  letterSpacing: "0.02em",
  border: `1px solid ${alpha(appTheme.palette.primary.main, 0.3)}`,
  color: alpha(appTheme.palette.text.primary, 0.8),
  backgroundColor: alpha(appTheme.palette.background.paper, 0.5),
  backdropFilter: "blur(5px)",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: alpha(appTheme.palette.primary.main, 0.1),
    borderColor: alpha(appTheme.palette.primary.main, 0.5),
    transform: "translateY(-1px)",
    boxShadow: `0 4px 12px ${alpha(appTheme.palette.primary.main, 0.2)}`,
  },
  "&.Mui-selected": {
    backgroundColor: appTheme.palette.primary.main,
    color: appTheme.palette.primary.contrastText,
    borderColor: appTheme.palette.primary.main,
    boxShadow: `0 4px 12px ${alpha(appTheme.palette.primary.main, 0.4)}`,
    "&:hover": {
      backgroundColor: appTheme.palette.primary.dark,
      borderColor: appTheme.palette.primary.dark,
      transform: "translateY(-2px)",
      boxShadow: `0 6px 16px ${alpha(appTheme.palette.primary.main, 0.5)}`,
    },
  },
  "&.MuiButton-contained": {
    backgroundColor: appTheme.palette.primary.main,
    color: appTheme.palette.primary.contrastText,
    borderColor: appTheme.palette.primary.main,
    boxShadow: `0 4px 12px ${alpha(appTheme.palette.primary.main, 0.4)}`,
    "&:hover": {
      backgroundColor: appTheme.palette.primary.dark,
      borderColor: appTheme.palette.primary.dark,
      transform: "translateY(-2px)",
      boxShadow: `0 6px 16px ${alpha(appTheme.palette.primary.main, 0.5)}`,
    },
  },
};

export function Toggle<T>({
  options,
  value,
  onChange,
  buttonStyles = {},
}: ToggleProps<T>) {
  const combinedStyles = { ...DEFAULT_BUTTON_STYLES, ...buttonStyles };

  return (
    <ButtonGroup
      sx={{
        "& .MuiButtonGroup-grouped": {
          "&:not(:last-of-type)": {
            borderRightColor: alpha(appTheme.palette.primary.main, 0.3),
          },
          "&:first-of-type": {
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
          },
          "&:last-of-type": {
            borderTopRightRadius: 8,
            borderBottomRightRadius: 8,
          },
        },
      }}
    >
      {options.map((option) => (
        <Button
          key={option.value as string}
          onClick={() => onChange(option.value)}
          variant={value === option.value ? "contained" : "outlined"}
          sx={combinedStyles}
        >
          {option.label}
        </Button>
      ))}
    </ButtonGroup>
  );
}
